/**
 * Destination-intent preservation for workspace context selection.
 *
 * When a user requests a workspace surface (e.g. `/projects`) but must first
 * choose among multiple accessible workspaces, the originally requested surface
 * must survive the selection so the user lands on `/workspaces/:id/projects` —
 * never silently on the workspace Dashboard.
 *
 * Security model: the "return target" is NOT an arbitrary URL. It is a single
 * value from a fixed allowlist of internal workspace surfaces. Because the only
 * thing carried through the selector is this enum token, open redirects,
 * protocol-relative URLs, `javascript:` URLs and cross-origin targets are
 * structurally impossible. The scoped destination is always reconstructed from
 * `(validatedWorkspaceId, surface)` AFTER the workspace is validated.
 */
import { customerRouteHref, type CustomerRouteTarget } from '@/lib/navigation/canonicalRoutes';

/** Allowlisted workspace surfaces that may be preserved as a return intent. */
export const WORKSPACE_RETURN_SURFACES = [
  'dashboard',
  'projects',
  'milestones',
  'tasks',
  'assets',
  'documents',
  'companies',
  'reports',
  'chat',
  'investments',
  'decisions',
  'users',
  'security',
  'settings',
] as const;

export type WorkspaceReturnSurface = (typeof WORKSPACE_RETURN_SURFACES)[number];

const SURFACE_SET: ReadonlySet<string> = new Set<string>(WORKSPACE_RETURN_SURFACES);

/**
 * Normalize an untrusted return-intent value to an allowlisted surface, or null.
 * Rejects anything that is not exactly one of the known surface tokens — this
 * intentionally rejects full URLs, external URLs, protocol-relative URLs and
 * `javascript:` URLs.
 */
export function normalizeReturnSurface(
  value: string | null | undefined,
): WorkspaceReturnSurface | null {
  if (!value) return null;
  const token = value.trim().toLowerCase();
  return SURFACE_SET.has(token) ? (token as WorkspaceReturnSurface) : null;
}

/**
 * Derive the intended surface from a requested internal path such as
 * `/projects`, `/tasks/123`, `/workspaces/:id/companies`. Returns null when the
 * path does not map to a known workspace surface.
 */
export function surfaceFromRequestedPath(
  path: string | null | undefined,
): WorkspaceReturnSurface | null {
  if (!path) return null;
  const clean = path.split('?')[0].split('#')[0];
  const segments = clean.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  // /workspaces/:id/<surface>/...
  if (segments[0] === 'workspaces' && segments[1]) {
    return normalizeReturnSurface(segments[2] ?? 'dashboard');
  }
  // flat legacy surface: /<surface>/...
  return normalizeReturnSurface(segments[0]);
}

/** Defense-in-depth guard: is this an internal, non-open-redirect path? */
export function isSafeInternalPath(value: string | null | undefined): boolean {
  if (!value) return false;
  const v = value.trim();
  if (!v.startsWith('/')) return false; // must be root-relative
  if (v.startsWith('//')) return false; // protocol-relative
  if (v.includes('\\')) return false; // backslash tricks
  if (/^\/[^/]*:/.test(v)) return false; // scheme-like before first slash
  return true;
}

/** Build the scoped canonical href for a validated workspace + intended surface. */
export function buildScopedSurfaceHref(
  workspaceId: string,
  surface: WorkspaceReturnSurface,
): string {
  return customerRouteHref(workspaceId, surface as CustomerRouteTarget);
}

/**
 * Build the onboarding handoff without accepting an arbitrary return URL.
 * The destination is carried as an allowlisted surface token and reconstructed
 * only after onboarding has completed for a validated workspace.
 */
export function buildOnboardingHref(
  workspaceId?: string | null,
  returnSurface?: string | null,
): string {
  const params = new URLSearchParams();
  if (workspaceId) params.set('workspaceId', workspaceId);

  const normalizedSurface = normalizeReturnSurface(returnSurface);
  if (normalizedSurface) params.set('returnTo', normalizedSurface);

  const query = params.toString();
  return query ? `/onboarding?${query}` : '/onboarding';
}
